import { CssBaseline, GlobalStyles, ThemeProvider, createTheme } from "@mui/material";
import * as React from "react";

import {
  dataDisplayCustomizations,
  dataGridCustomizations,
  inputsCustomizations,
  surfacesCustomizations,
} from "@/theme/customizations";

import { colorSchemes, shadows, shape, typography } from "./themePrimitives";

interface AppThemeProps {
  children: React.ReactNode;
}

export default function AppTheme(props: AppThemeProps) {
  const { children } = props;
  const theme = React.useMemo(
    () =>
      createTheme({
        cssVariables: {
          colorSchemeSelector: "data-mui-color-scheme",
          cssVarPrefix: "template",
        },
        defaultColorScheme: "dark",
        colorSchemes,
        typography,
        shadows,
        shape,
        components: {
          ...inputsCustomizations,
          ...dataGridCustomizations,
          ...surfacesCustomizations,
          ...dataDisplayCustomizations,
        },
      }),
    [],
  );

  return (
    <ThemeProvider theme={theme} disableTransitionOnChange>
      <CssBaseline />
      {/* Disable text selection across the admin UI, but keep editable form
          fields selectable so inputs stay usable. */}
      <GlobalStyles
        styles={{
          "html, body": {
            WebkitUserSelect: "none",
            MozUserSelect: "none",
            msUserSelect: "none",
            userSelect: "none",
          },
          "input, textarea, [contenteditable='true']": {
            WebkitUserSelect: "text",
            MozUserSelect: "text",
            msUserSelect: "text",
            userSelect: "text",
          },
          // Keep browser-autofilled inputs transparent in both color schemes
          // (Chrome/Safari otherwise paint their own autofill highlight -
          // yellow in light mode, a blue-grey like #266798 in dark mode -
          // via `-webkit-box-shadow`, most visible on the login page). The
          // long background-color transition delays that paint indefinitely;
          // text-fill-color preserves the typed color. `!important` is
          // required because the browser's autofill styling otherwise wins
          // even against an equally-specific author rule.
          "input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active, textarea:-webkit-autofill, textarea:-webkit-autofill:hover, textarea:-webkit-autofill:focus, select:-webkit-autofill":
            {
              transitionProperty: "background-color, color",
              transitionDelay: "99999s",
              WebkitTextFillColor: "inherit !important",
              WebkitBoxShadow: "0 0 0 100px transparent inset !important",
              boxShadow: "0 0 0 100px transparent inset !important",
              caretColor: "inherit",
            },
        }}
      />
      {children}
    </ThemeProvider>
  );
}
